import { createContext, useContext, useMemo, useState } from "react";
import { useApplicationDocument } from "../../storage/useApplicationDocument.js";

const PortfolioValueContext = createContext({ value: null, remembered: false, savePreference() {}, setValue() {}, setRemembered() {} });
const storagePrefix = "regulator:portfolio-value:";

export function PortfolioValueProvider({ children }) {
  const application = useApplicationDocument();
  const profileId = application.profile?.id;
  const [sessionValues, setSessionValues] = useState({});
  const [rememberedProfiles, setRememberedProfiles] = useState({});

  const storedValue = profileId && !Object.hasOwn(sessionValues, profileId) ? readStoredValue(profileId) : null;
  const value = profileId ? (Object.hasOwn(sessionValues, profileId) ? sessionValues[profileId] : storedValue) : null;
  const remembered = profileId ? (Object.hasOwn(rememberedProfiles, profileId) ? rememberedProfiles[profileId] : storedValue !== null) : false;

  const contextValue = useMemo(() => ({
    value,
    remembered,
    setValue(nextValue) {
      if (!profileId) return;
      setSessionValues((current) => ({ ...current, [profileId]: nextValue }));
      if (remembered) writeStoredValue(profileId, nextValue);
    },
    setRemembered(nextRemembered) {
      if (!profileId) return;
      setRememberedProfiles((current) => ({ ...current, [profileId]: nextRemembered }));
      if (nextRemembered) writeStoredValue(profileId, value);
      else globalThis.localStorage?.removeItem(`${storagePrefix}${profileId}`);
    },
    savePreference(nextValue, nextRemembered) {
      if (!profileId) return;
      const rememberValue = nextValue !== null && nextRemembered;
      setSessionValues((current) => ({ ...current, [profileId]: nextValue }));
      setRememberedProfiles((current) => ({ ...current, [profileId]: rememberValue }));
      if (rememberValue) writeStoredValue(profileId, nextValue);
      else globalThis.localStorage?.removeItem(`${storagePrefix}${profileId}`);
    }
  }), [profileId, remembered, value]);

  return <PortfolioValueContext.Provider value={contextValue}>{children}</PortfolioValueContext.Provider>;
}

export function usePortfolioValue() {
  return useContext(PortfolioValueContext);
}

export function amountForBasisPoints(portfolioValue, basisPoints) {
  if (portfolioValue === null) return null;
  return portfolioValue * basisPoints / 10000;
}

export function formatPortfolioAmount(amount) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(amount);
}

function readStoredValue(profileId) {
  const stored = globalThis.localStorage?.getItem(`${storagePrefix}${profileId}`);
  if (stored === null) return null;
  const value = Number(stored);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function writeStoredValue(profileId, value) {
  const key = `${storagePrefix}${profileId}`;
  if (value === null) globalThis.localStorage?.removeItem(key);
  else globalThis.localStorage?.setItem(key, String(value));
}
